import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import {
  Loader2,
  Send,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Terminal,
  MessageCircle,
  Mail,
} from 'lucide-react';

interface TestSubmissionStepProps {
  formId: string;
  onComplete: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export function TestSubmissionStep({ formId, onComplete, onSkip, onBack }: TestSubmissionStepProps) {
  const [testData, setTestData] = useState({
    name: 'Test User',
    email: 'test@example.com',
    message: 'This is a test message from FormTo onboarding!',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [result, setResult] = useState<{ telegram: boolean; email: boolean } | null>(null);
  const [showCurl, setShowCurl] = useState(false);
  const { toast } = useToast();

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const webhookUrl = `${apiUrl}/api/webhook/${formId}`;

  const curlCommand = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(testData, null, 2)}'`;

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await api.testSubmit(formId, testData);
      setResult(response);
      setIsSuccess(true);

      const channels = [];
      if (response.telegram) channels.push('Telegram');
      if (response.email) channels.push('Email');

      toast({
        title: 'Test sent successfully!',
        description:
          channels.length > 0
            ? `Check your ${channels.join(' and ')}`
            : 'No delivery channels configured yet',
      });

      setTimeout(() => onComplete(), 2000);
    } catch (error: any) {
      console.error('Test submission failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test submission',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCurl = () => {
    navigator.clipboard.writeText(curlCommand);
    toast({
      title: 'Copied',
      description: 'curl command copied to clipboard',
    });
  };

  if (isSuccess && result) {
    return (
      <Card className="mt-8">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-600 mb-4">Test Sent!</h3>

          {/* Delivery channels */}
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className={`flex items-center gap-2 ${result.telegram ? 'text-green-600' : 'text-gray-400'}`}>
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                Telegram {result.telegram ? '✓' : '(not configured)'}
              </span>
            </div>
            <div className={`flex items-center gap-2 ${result.email ? 'text-green-600' : 'text-gray-400'}`}>
              <Mail className="w-5 h-5" />
              <span className="text-sm font-medium">
                Email {result.email ? '✓' : '(not configured)'}
              </span>
            </div>
          </div>

          <p className="text-gray-600 text-sm">
            {result.telegram || result.email
              ? 'Check your connected channels for the test message!'
              : "No channels are configured yet. You can add them in the next step or later."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-8">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl">Test Your Form</CardTitle>
        <CardDescription>
          Send a test submission to verify everything works. The message will be marked as [TEST].
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Test form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={testData.name}
                onChange={(e) => setTestData({ ...testData, name: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={testData.email}
                onChange={(e) => setTestData({ ...testData, email: e.target.value })}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={testData.message}
                onChange={(e) => setTestData({ ...testData, message: e.target.value })}
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          </div>

          {/* Send button */}
          <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Test Submission
              </>
            )}
          </Button>

          {/* Alternative: curl command */}
          <div className="border-t pt-4">
            <button
              onClick={() => setShowCurl(!showCurl)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            >
              <Terminal className="w-4 h-4" />
              {showCurl ? 'Hide' : 'Show'} curl command
            </button>

            {showCurl && (
              <div className="mt-3">
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                    {curlCommand}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={handleCopyCurl}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Run this command in your terminal to test the webhook directly.
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={onSkip}
              disabled={isSubmitting}
            >
              Skip for now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
